import { useEffect, useRef, useState } from "react";
import { FiX, FiLoader, FiCheck, FiAlertCircle, FiExternalLink } from "react-icons/fi";
import { getOrderStatus } from "../api/checkout";

/**
 * Keepz checkout in a modal instead of a full-page redirect.
 *
 * Shows Keepz's own payment page, where the buyer picks: Apple Pay, Google Pay, bank
 * card, crypto, open banking or installments. Whatever they pick happens inside Keepz's
 * page, so the frame stays on their origin and never has to follow a bank's login,
 * which would refuse to be framed anyway.
 *
 * The frame is cross-origin, so its URL and navigation are unreadable to us. Completion
 * is detected exactly as the return page does it: by polling our own API. That means
 * this needs no cooperation from the page inside the frame.
 *
 * Keepz has not sanctioned framing in writing, and neither their page nor the bank's
 * 3DS page currently sends X-Frame-Options — but that can change without notice, so a
 * frame that hasn't loaded within LOAD_TIMEOUT_MS falls back to the redirect rather
 * than leaving someone staring at an empty box.
 *
 * Keepz's page is not responsive: below roughly 680px it clips rather than reflowing,
 * so the frame is sized to fit it and narrow viewports use the redirect instead
 * (see useCheckout). Do not shrink FRAME_MIN_WIDTH without re-checking the render.
 *
 * @param {{ open, orderId, checkoutUrl, amount, currency, isDark, onClose, onPaid }} props
 */
const POLL_STEPS = [2, 3, 3, 5, 5, 8];
const LOAD_TIMEOUT_MS = 10000;
/** Measured: Keepz's checkout clips below this. */
const FRAME_MIN_WIDTH = 700;

const CheckoutModal = ({
  open,
  orderId,
  checkoutUrl,
  amount,
  currency = "GEL",
  isDark,
  onClose,
  onPaid,
}) => {
  // loading | ready | confirming | paid | failed | blocked. The frame only exists in
  // the first two: once payment has sent it back to our own page, it has nothing
  // left to show.
  const [phase, setPhase] = useState("loading");
  const frameLoadedRef = useRef(false);
  const doneRef = useRef(false);
  const timerRef = useRef(null);
  const attemptRef = useRef(0);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const panel = "bg-bg-raised border-separator-default";

  useEffect(() => {
    if (!open || !orderId) return undefined;

    doneRef.current = false;
    attemptRef.current = 0;
    frameLoadedRef.current = false;
    setPhase("loading");

    // Polling our own API is the only completion signal available across origins.
    const poll = async () => {
      if (doneRef.current) return;
      try {
        const data = await getOrderStatus(orderId);
        if (data.paid) {
          doneRef.current = true;
          setPhase("paid");
          onPaidRef.current?.(data);
          return;
        }
        if (data.settled) {
          doneRef.current = true;
          setPhase("failed");
          return;
        }
      } catch {
        // Transient — keep polling. The reconciliation sweep is the real safety net.
      }
      const step = POLL_STEPS[Math.min(attemptRef.current, POLL_STEPS.length - 1)];
      attemptRef.current += 1;
      timerRef.current = setTimeout(poll, step * 1000);
    };
    timerRef.current = setTimeout(poll, POLL_STEPS[0] * 1000);

    // If the frame never loads, Keepz has probably started refusing to be framed.
    const loadWatch = setTimeout(() => {
      if (!frameLoadedRef.current && !doneRef.current) setPhase("blocked");
    }, LOAD_TIMEOUT_MS);

    return () => {
      doneRef.current = true;
      clearTimeout(timerRef.current);
      clearTimeout(loadWatch);
    };
  }, [open, orderId]);

  // Keepz redirects the frame when payment finishes, which lands our own return page
  // inside this modal. That page posts a message instead of rendering, so this is the
  // fastest signal we get — well ahead of the next poll.
  useEffect(() => {
    if (!open) return undefined;

    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "keepz:checkout-returned") return;
      if (event.data.orderId !== orderId || doneRef.current) return;

      // Tell the frame we heard it, so it doesn't fall back to seizing the whole tab.
      event.source?.postMessage(
        { type: "keepz:checkout-ack", orderId },
        window.location.origin
      );

      // The frame has done its job. Unmount it now — leaving it up shows a second copy
      // of Mintly nested inside this modal while we wait.
      setPhase("confirming");

      // Ask our own API what actually happened rather than trusting the redirect.
      getOrderStatus(orderId)
        .then((data) => {
          if (data.paid) {
            doneRef.current = true;
            clearTimeout(timerRef.current);
            setPhase("paid");
            onPaidRef.current?.(data);
          } else if (data.settled) {
            doneRef.current = true;
            clearTimeout(timerRef.current);
            setPhase("failed");
          }
          // Otherwise still settling: the poll already running will finish the job.
        })
        .catch(() => {});
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, orderId]);

  // Escape closes, and the page behind must not scroll under the modal.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-overlay-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Secure checkout"
    >
      <div
        className={`w-full rounded-radius-lg border overflow-hidden flex flex-col ${panel}`}
        style={{ maxWidth: FRAME_MIN_WIDTH + 24, maxHeight: "94vh" }}
      >
        <div
          className={`flex items-center justify-between px-5 py-3.5 shrink-0 border-b ${
            "border-separator-default"
          }`}
        >
          <div className="min-w-0">
            <p className={`text-subheadline-semibold ${baseText}`}>
              {phase === "paid" ? "Payment complete" : "Secure checkout"}
            </p>
            {amount != null && phase !== "paid" && (
              <p className={`text-footnote ${mutedText}`}>
                {Number(amount).toFixed(2)} {currency} · secured by Keepz
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-radius-xs transition ${
              "hover:bg-fill-quaternary"
            }`}
            aria-label="Close checkout"
          >
            <FiX className={`h-5 w-5 ${mutedText}`} />
          </button>
        </div>

        {(phase === "loading" || phase === "ready") && (
          <div className="relative flex-1 min-h-0">
            {phase === "loading" && (
              <div
                className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 ${panel}`}
              >
                <FiLoader className="h-8 w-8 text-accent-text animate-spin" />
                <p className={`text-subheadline ${mutedText}`}>Opening secure checkout…</p>
              </div>
            )}
            <iframe
              title="Keepz checkout"
              src={checkoutUrl}
              onLoad={() => {
                frameLoadedRef.current = true;
                setPhase((p) => (p === "loading" ? "ready" : p));
              }}
              className="w-full border-0 bg-white"
              style={{ height: "min(640px, 80vh)", minWidth: FRAME_MIN_WIDTH }}
              allow="payment *; clipboard-write"
            />
          </div>
        )}

        {phase === "confirming" && (
          <div className="flex flex-col items-center gap-3 px-6 py-16">
            <FiLoader className="h-8 w-8 text-accent-text animate-spin" />
            <p className={`text-subheadline ${mutedText}`}>Confirming your payment…</p>
          </div>
        )}

        {phase === "paid" && (
          <div className="flex flex-col items-center px-6 py-10">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent-default">
              <FiCheck className="h-8 w-8 text-accent-on" />
            </div>
            <p className={`max-w-xs text-center text-subheadline ${mutedText}`}>
              You now have access. Enjoy!
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-radius-sm bg-accent-default px-6 py-2.5 text-subheadline-semibold text-accent-on"
            >
              Continue
            </button>
          </div>
        )}

        {phase === "failed" && (
          <div className="flex flex-col items-center px-6 py-10">
            <FiAlertCircle className="mb-3 h-10 w-10 text-status-danger" />
            <p className={`max-w-xs text-center text-subheadline ${mutedText}`}>
              That payment didn't go through — you haven't been charged.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-radius-sm bg-accent-default px-6 py-2.5 text-subheadline-semibold text-accent-on"
            >
              Close
            </button>
          </div>
        )}

        {phase === "blocked" && (
          <div className="flex flex-col items-center px-6 py-10">
            <FiExternalLink className="mb-3 h-10 w-10 text-accent-text" />
            <p className={`max-w-xs text-center text-subheadline ${mutedText}`}>
              This payment needs a full page. Your order is still waiting — you won't be
              charged twice.
            </p>
            <button
              type="button"
              onClick={() => {
                window.location.href = checkoutUrl;
              }}
              className="mt-6 rounded-radius-sm bg-accent-default px-6 py-2.5 text-subheadline-semibold text-accent-on"
            >
              Continue to checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
